/* ============================================================
   cust-persona-events.js — site-wide behavioral event beacons
   ============================================================
   Loaded on every storefront page via layout/theme.liquid. Three beacons:

   1. SEARCH submit — fires every time a visitor submits the site search
      form (Enter or icon click). Posts to /api/landing/search-log.
   2. NAV CLICK — delegated handler on header / drawer / nav links.
      Captures label + href + chronological sequence within the session.
      Posts to /api/landing/nav-log.
   3. (FILTER PILL events fire from cust-v6-filter-controller.js — that
      file owns filter state transitions and beacons directly. Search
      and nav are owned here.)

   Session context:
     - Browser-generated UUID stored in sessionStorage. Cleared when
       the tab closes (so a new tab = new session). Reused across same-
       tab navigation.
     - Persona context pulled from window.vioriPersona.get() — set by
       cust-persona-attribution.js on persona-page landings.
     - Sequence index per session for NAV (1st click, 2nd click, etc.)
       to reconstruct the navigation path.

   All beacons use navigator.sendBeacon with a text/plain Blob to avoid
   CORS preflight. Falls back to fetch + no-cors + keepalive if sendBeacon
   isn't available. Failures are swallowed silently — the dashboard's
   Data Logs tab is best-effort; a missed beacon never breaks the page.

   Idempotent guard — safe to load twice. */

(function () {
  var INSTALLED_FLAG = "__viori_persona_events_installed__";
  var SESSION_KEY = "viori_persona_session_id";
  var SEQ_KEY = "viori_persona_nav_sequence";
  var WORKER_BASE = "https://api.viori.storepersona.com";

  if (window[INSTALLED_FLAG]) return;
  window[INSTALLED_FLAG] = true;

  // ---------- Session ID + sequence helpers --------------------------

  function getOrCreateSessionId() {
    try {
      var existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      var fresh = generateUuid();
      sessionStorage.setItem(SESSION_KEY, fresh);
      return fresh;
    } catch (e) {
      // sessionStorage blocked (Safari private mode, etc.) — generate
      // a per-pageload ID so beacons still fire. Sequence won't survive
      // navigation but that's the best we can do.
      return generateUuid();
    }
  }

  // RFC 4122 v4 — works without crypto.randomUUID in older browsers.
  function generateUuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      try { return crypto.randomUUID(); } catch (e) { /* fall through */ }
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === "x" ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function nextNavSequence() {
    try {
      var n = parseInt(sessionStorage.getItem(SEQ_KEY) || "0", 10);
      n = isNaN(n) ? 1 : n + 1;
      sessionStorage.setItem(SEQ_KEY, String(n));
      return n;
    } catch (e) {
      return 0;
    }
  }

  function getPersonaId() {
    try {
      if (window.vioriPersona && typeof window.vioriPersona.get === "function") {
        return window.vioriPersona.get() || null;
      }
    } catch (e) { /* swallow */ }
    return null;
  }

  // ---------- Beacon transport --------------------------------------

  function send(path, payload) {
    try {
      var url = WORKER_BASE + path;
      var body = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: "text/plain" });
        navigator.sendBeacon(url, blob);
        return;
      }
      if (typeof fetch === "function") {
        fetch(url, {
          method: "POST",
          mode: "no-cors",
          keepalive: true,
          headers: { "Content-Type": "text/plain" },
          body: body,
        }).catch(function () { /* swallow */ });
      }
    } catch (e) { /* swallow */ }
  }

  var SESSION_ID = getOrCreateSessionId();

  // ---------- 1. Search submit beacon -------------------------------

  // Stiletto theme search form selectors. The theme uses standard
  // Shopify search markup — form[action="/search"] with input[name="q"].
  // Hook via delegated submit on document so dynamically-added search
  // forms (e.g. mobile drawer search) are caught too.
  function maybeLogSearch(form) {
    try {
      if (!form || !form.action) return;
      var actionPath = "";
      try { actionPath = new URL(form.action, window.location.origin).pathname; }
      catch (e) { actionPath = String(form.action); }
      if (!/\/search$/i.test(actionPath)) return;
      var input = form.querySelector('input[name="q"]');
      if (!input) return;
      var q = String(input.value || "").trim();
      if (!q) return;
      send("/api/landing/search-log", {
        query_text: q,
        persona_id: getPersonaId(),
        session_id: SESSION_ID,
        landing_path: window.location.pathname,
        results_count: null,  // unknown at submit time
      });
    } catch (e) { /* swallow */ }
  }

  document.addEventListener("submit", function (e) {
    var form = e.target && e.target.closest && e.target.closest("form");
    if (form) maybeLogSearch(form);
  }, { capture: true });

  // ---------- 2. Nav click beacon -----------------------------------

  // Delegated click on every link in the header, drawer, and primary nav.
  // Selector list covers Stiletto's hamburger drawer + desktop nav. Cap
  // the label length defensively in case some link wraps an entire
  // marketing banner.
  var NAV_CONTAINER_SELECTOR = [
    "header",
    ".header",
    ".site-header",
    "nav",
    ".drawer",
    ".drawer__navigation",
    ".mobile-nav",
    ".hamburger",
    "[data-section-type='header']",
    // Persona-shell custom nav + drawer (sp_persona pages). The drawer
    // is rendered as <aside class="sp-nav__drawer"> at the section root,
    // a sibling of <nav class="sp-nav">, so neither `nav` nor a `.sp-nav`
    // ancestor match drawer-link clicks. Need both selectors.
    ".sp-nav",
    ".sp-nav__drawer",
    "[data-sp-nav-links]",
    "[data-sp-nav-drawer]",
  ].join(",");

  function isNavLink(a) {
    if (!a || a.tagName !== "A") return false;
    return !!a.closest(NAV_CONTAINER_SELECTOR);
  }

  function extractLabel(a) {
    var raw = (a.textContent || a.getAttribute("aria-label") || a.getAttribute("title") || "").trim();
    // Collapse whitespace + truncate.
    raw = raw.replace(/\s+/g, " ");
    return raw.slice(0, 200);
  }

  document.addEventListener("click", function (e) {
    try {
      var a = e.target && e.target.closest && e.target.closest("a");
      if (!a || !isNavLink(a)) return;
      var label = extractLabel(a);
      if (!label) return;
      var href = a.getAttribute("href") || "";
      send("/api/landing/nav-log", {
        link_label: label,
        link_href: href,
        persona_id: getPersonaId(),
        session_id: SESSION_ID,
        source_path: window.location.pathname,
        sequence_idx: nextNavSequence(),
      });
    } catch (err) { /* swallow */ }
  }, { capture: true });

  // ---------- Public hook for cust-v6-filter-controller.js ---------

  // Filter controller fires its own beacons via this helper so the
  // session context (id + persona) stays consistent across event types.
  window.vioriEvents = {
    logFilter: function (payload) {
      try {
        send("/api/landing/filter-log", {
          pill_key: String(payload && payload.pill_key || ""),
          pill_value: String(payload && payload.pill_value || ""),
          action: String(payload && payload.action || ""),
          resulting_state: payload && payload.resulting_state || null,
          persona_id: getPersonaId(),
          session_id: SESSION_ID,
          source_path: window.location.pathname,
        });
      } catch (e) { /* swallow */ }
    },
    sessionId: SESSION_ID,
  };
})();
